import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, Mail, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';

const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [errorMessage, setErrorMessage] = useState('');

  // Resend form state
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('idle'); // idle | loading | sent | error
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    if (!token) {
      setErrorMessage('No verification token found. Please use the link from your email.');
      setStatus('error');
      return;
    }

    authService.verifyEmail(token)
      .then(() => setStatus('success'))
      .catch((err) => {
        setErrorMessage(err.message || 'Email verification failed. The link may have expired.');
        setStatus('error');
      });
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendStatus('loading');
    setResendError('');
    try {
      await authService.resendVerification(resendEmail.trim());
      setResendStatus('sent');
    } catch (err) {
      setResendError(err.message || 'Failed to resend. Please try again.');
      setResendStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 pt-20 pb-12">
      <div className="w-full max-w-md">
        {/* Loading */}
        {status === 'loading' && (
          <div className="bg-white rounded-2xl shadow-lg p-10 text-center">
            <Loader className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('auth.verifying')}</h2>
            <p className="text-gray-500">Please wait a moment.</p>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="bg-white rounded-2xl shadow-lg p-10 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-9 h-9 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.verifiedTitle')}</h2>
            <p className="text-gray-600 mb-8">
              {t('auth.verifiedDesc')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
            >
              {t('auth.goToLogin')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="bg-white rounded-2xl shadow-lg p-10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-9 h-9 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.failedTitle')}</h2>
              <p className="text-gray-600">{errorMessage}</p>
            </div>

            {/* Resend form */}
            {resendStatus !== 'sent' && (
              <div className="border-t border-gray-100 pt-6">
                <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400" />
                  {t('auth.requestNew')}
                </p>
                <form onSubmit={handleResend} className="space-y-3">
                  <input
                    type="email"
                    required
                    placeholder="Enter your email address"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {resendError && (
                    <p className="text-xs text-red-600">{resendError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={resendStatus === 'loading'}
                    className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
                  >
                    {resendStatus === 'loading' ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        {t('auth.sending')}
                      </>
                    ) : (
                      t('auth.requestNew')
                    )}
                  </button>
                </form>
              </div>
            )}

            {resendStatus === 'sent' && (
              <div className="border-t border-gray-100 pt-6 text-center">
                <p className="text-sm text-green-700 font-medium">
                  {t('auth.verifiedTitle')}
                </p>
              </div>
            )}

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-primary-600 hover:underline">
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
